import * as THREE from 'three';

/**
 * Procedurally generated, tileable terrain and material textures.
 * All artwork is generated at runtime (original, no external assets).
 */

function makeRng(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Tileable value noise grid. */
function tileNoise(size: number, cells: number, seed: number): Float32Array {
  const rnd = makeRng(seed);
  const g = new Float32Array(cells * cells).map(() => rnd());
  const out = new Float32Array(size * size);
  for (let y = 0; y < size; y++)
    for (let x = 0; x < size; x++) {
      const fx = (x / size) * cells;
      const fy = (y / size) * cells;
      const ix = Math.floor(fx);
      const iy = Math.floor(fy);
      const tx = fx - ix;
      const ty = fy - iy;
      const sx = tx * tx * (3 - 2 * tx);
      const sy = ty * ty * (3 - 2 * ty);
      const a = g[(iy % cells) * cells + (ix % cells)];
      const b = g[(iy % cells) * cells + ((ix + 1) % cells)];
      const c = g[((iy + 1) % cells) * cells + (ix % cells)];
      const d = g[((iy + 1) % cells) * cells + ((ix + 1) % cells)];
      out[y * size + x] = a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy;
    }
  return out;
}

function fbmTile(size: number, seed: number, octaves = 5, base = 4): Float32Array {
  const out = new Float32Array(size * size);
  let amp = 1;
  let norm = 0;
  for (let o = 0; o < octaves; o++) {
    const n = tileNoise(size, base << o, seed + o * 31);
    for (let i = 0; i < out.length; i++) out[i] += n[i] * amp;
    norm += amp;
    amp *= 0.5;
  }
  for (let i = 0; i < out.length; i++) out[i] /= norm;
  return out;
}

type RGB = [number, number, number];
const lerp = (a: RGB, b: RGB, t: number): RGB => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];

function canvasTexture(size: number, paint: (img: ImageData) => void, srgb = true): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d')!;
  const img = ctx.createImageData(size, size);
  paint(img);
  ctx.putImageData(img, 0, 0);
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.anisotropy = 4;
  if (srgb) t.colorSpace = THREE.SRGBColorSpace;
  t.generateMipmaps = true;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  return t;
}

function paintLayer(size: number, seed: number, dark: RGB, light: RGB, speck: RGB, speckRate: number, contrast = 1): (img: ImageData) => void {
  return (img) => {
    const n = fbmTile(size, seed, 6, 4);
    const fine = fbmTile(size, seed + 99, 3, 32);
    const rnd = makeRng(seed + 7);
    for (let i = 0; i < size * size; i++) {
      let t = Math.min(1, Math.max(0, (n[i] - 0.5) * 1.8 * contrast + 0.5 + (fine[i] - 0.5) * 0.5));
      let c = lerp(dark, light, t);
      if (rnd() < speckRate) c = lerp(c, speck, 0.6 + rnd() * 0.4);
      const j = i * 4;
      img.data[j] = c[0];
      img.data[j + 1] = c[1];
      img.data[j + 2] = c[2];
      img.data[j + 3] = 255;
      t = 0;
    }
  };
}

function paintGrass(size: number, seed: number): (img: ImageData) => void {
  return (img) => {
    const n = fbmTile(size, seed, 6, 4);
    const blades = fbmTile(size, seed + 5, 2, 64);
    const rnd = makeRng(seed);
    for (let i = 0; i < size * size; i++) {
      const t = Math.min(1, Math.max(0, (n[i] - 0.5) * 2.2 + 0.5));
      let c = lerp([62, 92, 34], [118, 146, 58], t);
      c = lerp(c, [150, 160, 70], Math.max(0, blades[i] - 0.6) * 1.2);
      if (rnd() < 0.05) c = lerp(c, [40, 70, 25], 0.5);
      if (rnd() < 0.004) c = [190, 180, 90];
      const j = i * 4;
      img.data[j] = c[0];
      img.data[j + 1] = c[1];
      img.data[j + 2] = c[2];
      img.data[j + 3] = 255;
    }
  };
}

function paintRock(size: number, seed: number): (img: ImageData) => void {
  return (img) => {
    const n = fbmTile(size, seed, 6, 3);
    const cracks = tileNoise(size, 12, seed + 3);
    for (let i = 0; i < size * size; i++) {
      const t = Math.min(1, Math.max(0, (n[i] - 0.5) * 2 + 0.5));
      let c = lerp([88, 82, 76], [150, 144, 132], t);
      const cr = Math.abs(cracks[i] - 0.5);
      if (cr < 0.03) c = lerp(c, [50, 46, 42], 0.7);
      const j = i * 4;
      img.data[j] = c[0];
      img.data[j + 1] = c[1];
      img.data[j + 2] = c[2];
      img.data[j + 3] = 255;
    }
  };
}

export interface TerrainTextures {
  grass: THREE.Texture;
  dirt: THREE.Texture;
  rock: THREE.Texture;
  sand: THREE.Texture;
  snow: THREE.Texture;
  forest: THREE.Texture;
  road: THREE.Texture;
}

let cached: TerrainTextures | null = null;

export function terrainTextures(resolution = 512): TerrainTextures {
  if (cached) return cached;
  cached = {
    grass: canvasTexture(resolution, paintGrass(resolution, 11)),
    dirt: canvasTexture(resolution, paintLayer(resolution, 23, [96, 72, 48], [146, 114, 78], [70, 56, 40], 0.06)),
    rock: canvasTexture(resolution, paintRock(resolution, 37)),
    sand: canvasTexture(resolution, paintLayer(resolution, 41, [196, 170, 120], [232, 210, 160], [170, 150, 110], 0.03, 0.6)),
    snow: canvasTexture(resolution, paintLayer(resolution, 53, [210, 218, 228], [248, 250, 255], [190, 200, 215], 0.02, 0.5)),
    forest: canvasTexture(resolution, paintLayer(resolution, 67, [44, 58, 26], [84, 92, 44], [70, 52, 30], 0.08)),
    road: canvasTexture(resolution, paintLayer(resolution, 79, [120, 96, 66], [168, 140, 100], [96, 90, 84], 0.12, 1.3)),
  };
  return cached;
}

// ---------------------------------------------------------------------------------------------
// Building surface textures: wood, brick, stone, roof tiles, thatch and tent canvas. Each is a
// tileable greyscale "multiplier" over the model's own colour (SURF_UNIT = no change), so one set
// works for every civilisation's palette. One texture repeat covers one tile of the map.

/** Stored value that leaves the colour unchanged; the shader multiplies by value / SURF_UNIT. */
export const SURF_UNIT = 160;
export const SURF_LAYERS = ['wood', 'brick', 'stone', 'tile', 'thatch', 'canvas'] as const;

const clampByte = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
/** Distance (0..0.5) of t from the nearest whole number: 0 on a joint line. */
const toEdge = (t: number) => Math.abs(t - Math.round(t));

function woodLayer(size: number): Uint8Array {
  const out = new Uint8Array(size * size);
  const n = fbmTile(size, 211, 4, 4);
  const grainWarp = fbmTile(size, 212, 3, 8);
  const rnd = makeRng(213);
  const planks = 6;
  // Per plank: a tone and where its end joint falls.
  const tone = Array.from({ length: planks }, () => 0.9 + rnd() * 0.2);
  const joint = Array.from({ length: planks }, () => rnd());
  for (let y = 0; y < size; y++) {
    const pv = (y / size) * planks;
    const p = Math.floor(pv);
    const across = toEdge(pv);
    for (let x = 0; x < size; x++) {
      const i = y * size + x;
      const u = x / size;
      // Grain: long wavy lines along the plank.
      const g = Math.sin((pv * 9 + grainWarp[i] * 3.2) * Math.PI * 2) * 0.5 + 0.5;
      let v = tone[p] * (0.88 + g * 0.16) * (0.92 + (n[i] - 0.5) * 0.25);
      if (across < 0.035) v *= 0.55; // gap between planks
      if (toEdge(u - joint[p]) < 0.004) v *= 0.6; // butt joint
      // Knots, now and then.
      out[i] = clampByte(SURF_UNIT * v);
    }
  }
  for (let k = 0; k < 5; k++) {
    const cx = Math.floor(rnd() * size);
    const cy = Math.floor(rnd() * size);
    const r = 2 + rnd() * 3;
    for (let dy = -6; dy <= 6; dy++)
      for (let dx = -6; dx <= 6; dx++) {
        const d = Math.hypot(dx, dy * 1.6);
        if (d > r * 1.6) continue;
        const i = ((cy + dy + size) % size) * size + ((cx + dx + size) % size);
        out[i] = clampByte(out[i] * (d < r ? 0.6 : 0.85));
      }
  }
  return out;
}

function brickLayer(size: number): Uint8Array {
  const out = new Uint8Array(size * size);
  const n = fbmTile(size, 221, 4, 8);
  const rnd = makeRng(222);
  const rows = 12;
  const cols = 4;
  const tone = Array.from({ length: rows * cols }, () => 0.84 + rnd() * 0.3);
  for (let y = 0; y < size; y++) {
    const rv = (y / size) * rows;
    const r = Math.floor(rv);
    const fy = rv - r;
    for (let x = 0; x < size; x++) {
      const cv = (x / size) * cols + (r % 2 ? 0.5 : 0);
      const c = Math.floor(cv) % cols;
      const fx = cv - Math.floor(cv);
      const i = y * size + x;
      const mortar = fy < 0.12 || fx < 0.035;
      // Mortar is pale; bricks vary from brick to brick, with a softer edge just inside the joint.
      const edge = Math.min(fy - 0.12, 1 - fy, fx - 0.035, 1 - fx) * 8;
      const v = mortar ? 1.32 + (n[i] - 0.5) * 0.2 : tone[r * cols + c] * (0.92 + Math.min(1, edge) * 0.08) * (0.94 + (n[i] - 0.5) * 0.3);
      out[i] = clampByte(SURF_UNIT * v);
    }
  }
  return out;
}

function stoneLayer(size: number): Uint8Array {
  const out = new Uint8Array(size * size);
  const n = fbmTile(size, 231, 5, 6);
  const chips = fbmTile(size, 232, 2, 32);
  const rnd = makeRng(233);
  const rows = 4;
  // Coursed rubble: each course has blocks of random width.
  const cuts: number[][] = [];
  for (let r = 0; r < rows; r++) {
    // Joints across one repeat, spaced at least a fifth of a tile apart (also across the wrap).
    const start = rnd();
    const list: number[] = [];
    let at = 0;
    while (true) {
      const w = 0.2 + rnd() * 0.18;
      if (at + w > 1 - 0.2) break;
      at += w;
      list.push(at);
    }
    cuts.push([0, ...list].map((c) => (c + start) % 1).sort((a, b) => a - b));
  }
  const tone = Array.from({ length: 64 }, () => 0.82 + rnd() * 0.32);
  for (let y = 0; y < size; y++) {
    const rv = (y / size) * rows;
    const r = Math.floor(rv);
    const fy = rv - r;
    for (let x = 0; x < size; x++) {
      const u = x / size;
      const i = y * size + x;
      let block = 0;
      let joint = 1;
      for (let k = 0; k < cuts[r].length; k++) {
        const d = Math.min(Math.abs(u - cuts[r][k]), 1 - Math.abs(u - cuts[r][k]));
        joint = Math.min(joint, d);
        if (u >= cuts[r][k]) block = k;
      }
      const mortar = fy < 0.05 || fy > 0.97 || joint < 0.008;
      const bevel = Math.min(1, Math.min(fy - 0.05, 0.97 - fy, joint - 0.008) * 12);
      let v = mortar ? 0.6 : tone[(r * 13 + block * 7) % 64] * (0.78 + bevel * 0.22) * (0.9 + (n[i] - 0.5) * 0.45);
      if (!mortar && chips[i] > 0.78) v *= 0.93;
      out[i] = clampByte(SURF_UNIT * v);
    }
  }
  return out;
}

function tileLayer(size: number): Uint8Array {
  const out = new Uint8Array(size * size);
  const n = fbmTile(size, 241, 4, 8);
  const rnd = makeRng(242);
  const rows = 10;
  const cols = 6;
  const tone = Array.from({ length: rows * cols }, () => 0.86 + rnd() * 0.24);
  for (let y = 0; y < size; y++) {
    const rv = (y / size) * rows;
    const r = Math.floor(rv);
    const fy = rv - r;
    for (let x = 0; x < size; x++) {
      const cv = (x / size) * cols + (r % 2 ? 0.5 : 0);
      const c = Math.floor(cv) % cols;
      const fx = cv - Math.floor(cv);
      const i = y * size + x;
      // Each row overlaps the one below: lit at the top, shadowed along its lower edge.
      let v = tone[r * cols + c] * (0.78 + fy * 0.32);
      if (fy > 0.9) v *= 0.62;
      if (fx < 0.04 || fx > 0.97) v *= 0.7;
      v *= 0.93 + (n[i] - 0.5) * 0.25;
      out[i] = clampByte(SURF_UNIT * v);
    }
  }
  return out;
}

function thatchLayer(size: number): Uint8Array {
  const out = new Uint8Array(size * size);
  const rnd = makeRng(251);
  const coarse = fbmTile(size, 252, 4, 4);
  // Straws: thin vertical streaks of random brightness and length.
  const straw = new Float32Array(size * size).fill(1);
  for (let k = 0; k < size * 6; k++) {
    const x = Math.floor(rnd() * size);
    const y0 = Math.floor(rnd() * size);
    const len = 8 + Math.floor(rnd() * 26);
    const b = 0.72 + rnd() * 0.56;
    for (let d = 0; d < len; d++) straw[((y0 + d) % size) * size + x] = b;
  }
  const bundles = 5;
  const wobble = fbmTile(size, 253, 3, 4);
  for (let y = 0; y < size; y++) {
    const row = y * size;
    for (let x = 0; x < size; x++) {
      const i = row + x;
      // Bundle ends cast a soft shadow on the course below; the edge wanders, as laid straw does.
      const fy = ((y / size) * bundles + (wobble[i] - 0.5) * 0.6 + 5) % 1;
      const shade = fy < 0.2 ? 0.82 + fy * 0.9 : 1;
      out[i] = clampByte(SURF_UNIT * straw[i] * shade * (0.9 + (coarse[i] - 0.5) * 0.35));
    }
  }
  return out;
}

function canvasLayer(size: number): Uint8Array {
  const out = new Uint8Array(size * size);
  const folds = fbmTile(size, 261, 3, 3);
  const n = fbmTile(size, 262, 2, 64);
  for (let y = 0; y < size; y++)
    for (let x = 0; x < size; x++) {
      const i = y * size + x;
      // Plain weave: alternating over/under threads.
      const weave = ((x >> 1) + (y >> 1)) % 2 ? 1.04 : 0.96;
      // Panels sewn together every third of a tile: a stitched seam with a slight shadow beside it.
      const e = toEdge((x / size) * 3);
      const seam = e < 0.008 ? (y % 6 < 3 ? 0.68 : 0.8) : e < 0.03 ? 0.9 : 1;
      // Loose folds sag between the seams.
      const sag = 0.94 + Math.sin(((x / size) * 3) * Math.PI) * 0.06;
      out[i] = clampByte(SURF_UNIT * weave * seam * sag * (0.9 + (folds[i] - 0.5) * 0.5) * (0.97 + (n[i] - 0.5) * 0.1));
    }
  return out;
}

/** The six surface layers as greyscale bytes (size x size each), in SURF_LAYERS order. */
export function surfaceTextureData(size = 256): Uint8Array[] {
  return [woodLayer(size), brickLayer(size), stoneLayer(size), tileLayer(size), thatchLayer(size), canvasLayer(size)];
}

let surfaceSet: THREE.DataArrayTexture | null = null;

/** All surface layers in one texture array (one red channel per texel), with mipmaps. */
export function surfaceTextures(size = 256): THREE.DataArrayTexture {
  if (surfaceSet) return surfaceSet;
  const layers = surfaceTextureData(size);
  const data = new Uint8Array(size * size * layers.length);
  layers.forEach((l, k) => data.set(l, k * size * size));
  const t = new THREE.DataArrayTexture(data, size, size, layers.length);
  t.format = THREE.RedFormat;
  t.type = THREE.UnsignedByteType;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.magFilter = THREE.LinearFilter;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.generateMipmaps = true;
  t.anisotropy = 4;
  t.needsUpdate = true;
  surfaceSet = t;
  return t;
}
